/**
 * Format a quantity into a layer-breakdown string.
 *
 *   formatLayers(130, 30) -> "4 lagen van 30 + 1 laag van 10"
 *   formatLayers(120, 30) -> "4 lagen van 30"
 *   formatLayers(10, 30)  -> "1 laag van 10"
 *   formatLayers(30, 30)  -> "1 laag van 30"
 */
export function formatLayers(quantity, unitsPerLayer) {
    const qty = Number(quantity) || 0;
    const upl = Number(unitsPerLayer) || 0;
    if (qty <= 0) return "—";
    if (upl <= 0) return `${qty} stuks`;
    const full = Math.floor(qty / upl);
    const rem = qty - full * upl;
    const parts = [];
    if (full > 0) {
        parts.push(`${full} ${full === 1 ? "laag" : "lagen"} van ${upl}`);
    }
    if (rem > 0) {
        parts.push(`1 laag van ${rem}`);
    }
    return parts.join(" + ");
}

/**
 * NL-vriendelijk datum-formaat.
 *   formatDate("2026-07-01") → "1 jul 2026"
 *   formatDate("2026-07-01T15:30:00Z", { withTime: true }) → "1 jul 2026, 15:30"
 *   formatDate(null) → "—"
 */
export function formatDate(value, { withTime = false } = {}) {
    if (!value) return "—";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const opts = { day: "numeric", month: "short", year: "numeric" };
    if (withTime) {
        opts.hour = "2-digit";
        opts.minute = "2-digit";
    }
    return d.toLocaleString("nl-NL", opts);
}

/**
 * Relatieve tijdsweergave: "5 min geleden", "2 uur geleden", "gisteren", "3 dagen geleden".
 * Voor recentere events; fallback naar formatDate voor oudere.
 */
export function formatRelative(value) {
    if (!value) return "—";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "zojuist";
    if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
    if (diff < 2 * 86400) return "gisteren";
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} dagen geleden`;
    return formatDate(d);
}
